import _ from 'lodash';
import uuid from 'uuid/v4';
import { RootScopeEvents } from '../../../../common/services/root-scope-types';
import { determineReceiverLocatedStatus } from '../../../../modules/core/helpers/tracking-helpers';
import { AbstractRealtimeComponent } from '../../abstract-realtime-component';
export default class PaymentDoctorRealtimeComponent extends AbstractRealtimeComponent {
    /* @ngInject */
    constructor($rootScope, modalsService, $translate, institutionHelper, currentUserHelper, // still in JS
    User, Institution, notificationFactory, // still in JS
    doctorRealtimeActions, VideoProviderFactory, $ngRedux) {
        super();
        this.$rootScope = $rootScope;
        this.modalsService = modalsService;
        this.$translate = $translate;
        this.institutionHelper = institutionHelper;
        this.currentUserHelper = currentUserHelper;
        this.User = User;
        this.Institution = Institution;
        this.notificationFactory = notificationFactory;
        this.doctorRealtimeActions = doctorRealtimeActions;
        this.VideoProviderFactory = VideoProviderFactory;
        this.$ngRedux = $ngRedux;
    }
    create() {
        return this;
    }
    reset() {
        this.modalsService.close();
    }
    onLeave(_person) {
        /**
         * Do nothing.
         * Keep the extension open because provider or patient could leave and rejoin multiple times on unstable connection.
         */
    }
    onMessage(message, person) {
        switch (message.action) {
            case 'paymentProcessing': {
                this.modalsService.scope.status = 'processing';
                break;
            }
            case 'paymentSuccess': {
                this.onPaymentSuccess(message, person);
                break;
            }
            case 'paymentError': {
                this.modalsService.scope.status = 'error';
                break;
            }
            case 'close': {
                this.notificationFactory.showCommon('warning', 'payment-ended');
                this.modalsService.close();
                break;
            }
            default:
                throw new Error(`Unhandled payment message case: ${message.action}`);
        }
    }
    async open({ peopleAvailable, }) {
        if (peopleAvailable != null &&
            peopleAvailable.getMobileUsers().length > 0) {
            this.notificationFactory.showCommon('info', 'Payment currently does not support mobile devices');
            return;
        }
        const preferredCurrency = await this.getPreferredCurrency();
        this.peopleAvailable = peopleAvailable;
        this.target = this.peopleAvailable.models[0];
        this.currency = preferredCurrency || 'USD';
        await this.selectAmount();
    }
    onPaymentSuccess(message, person) {
        if (!this.target.isEqualTo(person)) {
            return;
        }
        const receiverLocatedStatus = determineReceiverLocatedStatus(person.uuid, this.$ngRedux.getState());
        this.$rootScope.$emit(RootScopeEvents.PAYMENT.FINISHED, {
            session_id: _.get(this.VideoProviderFactory, 'activeProvider.callSession.id', null),
            receiver_located: receiverLocatedStatus,
        });
        this.modalsService.scope.status = 'success';
    }
    getPaymentPopupHeader() {
        return this.$translate.instant('apps.payment.action.select-amount.title{username}', {
            username: this.target.state.username,
        });
    }
    openPaymentAmountPopup() {
        this.modalsService.open('doctorPaymentAmount', {
            header: this.getPaymentPopupHeader(),
            target: this.target,
            currency: this.currency,
            multipleAllowed: true,
            onCancel: () => {
                this.modalsService.close();
            },
            onSubmit: async (amount, currency) => {
                this.amount = amount;
                this.currency = currency;
                await this.askForPayment(this.peopleAvailable.models[0]);
            },
        });
    }
    openPaymentStatusPopup() {
        this.modalsService.open('doctorPaymentStatus', {
            header: this.getPaymentPopupHeader(),
            target: this.target,
            status: 'processing',
            multipleAllowed: true,
            onCancel: (notify) => {
                this.onCancel(notify);
                this.modalsService.close();
            },
        });
    }
    async selectAmount() {
        const institution = await this.institutionHelper.getCurrentInstitution();
        if (institution.chargeOnBehalfOfInstitution) {
            if (this.currentUserHelper.user.institutionMember.role === 'owner') {
                if (this.currentUserHelper.user.stripeConnect != null) {
                    this.openPaymentAmountPopup();
                }
                else {
                    this.notificationFactory.showCommon('info', 'payment-not-setup');
                }
            }
            else {
                const res = await this.Institution.prototype$hasInstitutionOwnerStripeConnect({
                    id: institution.id,
                }).$promise;
                if (res.hasStripeConnect) {
                    this.openPaymentAmountPopup();
                }
                else {
                    this.notificationFactory.showCommon('info', 'payment-not-setup');
                }
            }
        }
        else if (this.currentUserHelper.user.stripeConnect != null) {
            this.openPaymentAmountPopup();
        }
        else {
            this.notificationFactory.showCommon('info', 'payment-not-setup');
        }
    }
    async getPreferredCurrency() {
        const institution = await this.institutionHelper.getCurrentInstitution();
        if (institution.isNull() || !institution.chargeOnBehalfOfInstitution) {
            return this.currentUserHelper.user.preferredCurrency;
        }
        const { currency } = await this.User.prototype$getPreferredCurrency({ id: this.currentUserHelper.user.id }, {
            institutionId: institution.id,
        }).$promise;
        return currency;
    }
    async askForPayment(person) {
        if (person.isOnMobileApp()) {
            this.notificationFactory.showCommon('error', `Payment is currently not compatible with mobile users`);
            return;
        }
        this.target = person;
        this.openPaymentStatusPopup();
        const institution = await this.institutionHelper.getCurrentInstitution();
        try {
            let idempotencyKey = window.sessionStorage.getItem('idempotency-key.ask-for-payment.payment-intent');
            if (!idempotencyKey) {
                idempotencyKey = uuid();
                window.sessionStorage.setItem('idempotency-key.ask-for-payment.payment-intent', idempotencyKey);
            }
            const paymentIntent = await this.User.prototype$createPaymentIntent({ id: this.currentUserHelper.user.id }, {
                amount: this.amount,
                currency: this.currency,
                institutionId: institution.id,
                idempotencyKey,
            }).$promise;
            const paymentRequestMessage = {
                componentName: 'payment',
                action: 'paymentRequest',
                content: {
                    amount: this.amount,
                    clientSecret: paymentIntent.client_secret,
                    connectedStripePublishable: paymentIntent.metadata.stripeConnectPublishableKey,
                    currency: this.currency,
                },
            };
            this.doctorRealtimeActions.sendToTarget(this.target, paymentRequestMessage);
        }
        catch (err) {
            const errMessage = _.get(err, 'data.error.message') || 'payment-error';
            this.openPaymentAmountPopup();
            this.notificationFactory.showCommon('error', errMessage, null, 5000);
        }
        finally {
            window.sessionStorage.removeItem('idempotency-key.ask-for-payment.payment-intent');
        }
    }
    onCancel(notify) {
        if (notify) {
            this.doctorRealtimeActions.sendToTarget(this.target, {
                action: 'close',
                componentName: 'payment',
            });
        }
    }
}
